import { useEffect, useRef, useState } from 'react';
import type { FirestoreError, QuerySnapshot } from 'firebase/firestore';

import { FIRESTORE_ENABLED } from '@/config/env';
import { db } from '@/lib/firebase';

export interface StreamConfig {
  onPermissionDenied?: () => void;
}

let config: StreamConfig = {};

export function configureFirestoreStream(next: StreamConfig) {
  config = next;
}

export function reportStreamError(error: FirestoreError): void {
  if (error.code === 'permission-denied') config.onPermissionDenied?.();
}

export interface StreamState<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
}

/** The two operators this app queries with. Anything else needs a composite index. */
export type FilterOperator = '==' | 'array-contains';

export type Filter = readonly [field: string, operator: FilterOperator, value: unknown];

export interface StreamOptions<T> {
  path: string;
  parse: (id: string, raw: Record<string, unknown>) => T | null;
  limit: number;
  orderByField?: string;
  direction?: 'asc' | 'desc';
  filters?: readonly Filter[];
  enabled?: boolean;
}

interface QuerySpec {
  path: string;
  limit: number;
  orderByField?: string;
  direction: 'asc' | 'desc';
  filters: Filter[];
}

interface RawRow {
  id: string;
  raw: Record<string, unknown>;
}

/** What one shared listener knows, before any hook has parsed it. */
interface SharedState {
  rows: RawRow[];
  loading: boolean;
  error: Error | null;
}

type SharedListener = (state: SharedState) => void;

interface SharedSubscription {
  state: SharedState;
  listeners: Set<SharedListener>;
  stop?: () => void;
  cancelled: boolean;
}

/** One live listener per distinct query, shared by every hook that asks for it. */
const subscriptions = new Map<string, SharedSubscription>();

/** Subscribes to a Firestore query, loading the 620 kB SDK on demand. */
async function subscribe(
  spec: QuerySpec,
  onData: (snapshot: QuerySnapshot) => void,
  onError: (error: FirestoreError) => void,
): Promise<() => void> {
  const { collection, limit, onSnapshot, orderBy, query, where } = await import(
    'firebase/firestore'
  );
  const constraints = [
    ...spec.filters.map(([field, operator, value]) => where(field, operator, value)),
    ...(spec.orderByField ? [orderBy(spec.orderByField, spec.direction)] : []),
    limit(spec.limit),
  ];
  return onSnapshot(query(collection(await db(), spec.path), ...constraints), onData, onError);
}

function publish(subscription: SharedSubscription, state: SharedState) {
  subscription.state = state;
  subscription.listeners.forEach((listener) => listener(state));
}

/** Opens the query once, and hands every later hook the state the first one already has. */
function attach(key: string, spec: QuerySpec, listener: SharedListener): () => void {
  let subscription = subscriptions.get(key);
  if (!subscription) {
    const created: SharedSubscription = {
      state: { rows: [], loading: true, error: null },
      listeners: new Set(),
      cancelled: false,
    };
    subscriptions.set(key, created);
    subscription = created;
    subscribe(
      spec,
      (snapshot) =>
        publish(created, {
          rows: snapshot.docs.map((entry) => ({ id: entry.id, raw: entry.data() as Record<string, unknown> })),
          loading: false,
          error: null,
        }),
      (error) => {
        reportStreamError(error);
        publish(created, { rows: [], loading: false, error });
      },
    )
      // The SDK loads asynchronously
      .then((stop) => (created.cancelled ? stop() : void (created.stop = stop)))
      .catch((error: Error) => publish(created, { rows: [], loading: false, error }));
  }

  const shared = subscription;
  shared.listeners.add(listener);
  listener(shared.state);

  return () => {
    shared.listeners.delete(listener);
    if (shared.listeners.size > 0) return;
    shared.cancelled = true;
    shared.stop?.();
    subscriptions.delete(key);
  };
}

function parseRows<T>(rows: RawRow[], parse: StreamOptions<T>['parse']): T[] {
  return rows.map((row) => parse(row.id, row.raw)).filter((row): row is T => row !== null);
}

export function useFirestoreStream<T>({
  path,
  parse,
  limit,
  orderByField,
  direction = 'desc',
  filters,
  enabled = true,
}: StreamOptions<T>): StreamState<T> {
  const [state, setState] = useState<StreamState<T>>({
    data: [],
    loading: enabled && FIRESTORE_ENABLED,
    error: null,
  });

  const filterKey = JSON.stringify(filters ?? []);

  const active = enabled && FIRESTORE_ENABLED;

  // The latest parser; a new function identity does not reopen the query.
  const parseRef = useRef(parse);
  parseRef.current = parse;

  useEffect(() => {
    if (!active) {
      setState({ data: [], loading: false, error: null });
      return;
    }

    const spec: QuerySpec = {
      path,
      limit,
      orderByField,
      direction,
      filters: JSON.parse(filterKey) as Filter[],
    };

    return attach(JSON.stringify(spec), spec, (shared) =>
      setState({
        data: parseRows(shared.rows, parseRef.current),
        loading: shared.loading,
        error: shared.error,
      }),
    );
  }, [active, path, filterKey, orderByField, direction, limit]);

  return state;
}
